import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  AdminMember,
  AdminOrganization,
  AdminRegistrationCode,
} from "src/Types/auth.types";
import {
  getAdminMembers,
  getAdminOrganizations,
  revokeAdminRegistrationCode,
  startAdminOrganizationScan,
} from "src/utils/apicalls";
import AccountsTable from "./AccountsTable";
import CitiesTable from "./CitiesTable";
import CreateKeyDialog from "./CreateKeyDialog";
import { getApiErrorMessage } from "src/utils/apiError";

type AdminTab = "cities" | "accounts";

type Notice = { severity: "success" | "error"; text: string } | null;

type PendingRevoke = {
  code: AdminRegistrationCode;
  organization: AdminOrganization;
} | null;

const Admin: React.FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<AdminTab>("cities");
  const [organizations, setOrganizations] = useState<AdminOrganization[]>([]);
  const [members, setMembers] = useState<AdminMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState<PendingRevoke>(null);
  const [revoking, setRevoking] = useState(false);
  const [scanningId, setScanningId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [nextOrganizations, nextMembers] = await Promise.all([
        getAdminOrganizations(),
        getAdminMembers(),
      ]);
      setOrganizations(nextOrganizations);
      setMembers(nextMembers);
      setLoadError(null);
    } catch (error) {
      setLoadError(getApiErrorMessage(error, t, "admin.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRunScan = async (organization: AdminOrganization) => {
    setScanningId(organization.id);
    setNotice(null);
    try {
      await startAdminOrganizationScan(organization.id);
      setNotice({
        severity: "success",
        text: t("admin.scanQueued", { name: organization.name }),
      });
      await load();
    } catch (error) {
      setNotice({
        severity: "error",
        text: getApiErrorMessage(error, t, "admin.scanError", {
          name: organization.name,
        }),
      });
    } finally {
      setScanningId(null);
    }
  };

  const confirmRevoke = async () => {
    if (!pendingRevoke) return;
    setRevoking(true);
    try {
      await revokeAdminRegistrationCode(pendingRevoke.code.id);
      setNotice({
        severity: "success",
        text: t("admin.keyRevoked", { name: pendingRevoke.organization.name }),
      });
      setPendingRevoke(null);
      await load();
    } catch (error) {
      setNotice({
        severity: "error",
        text: getApiErrorMessage(error, t, "admin.revokeError"),
      });
    } finally {
      setRevoking(false);
    }
  };

  return (
    <Box sx={{ width: "100%", px: { xs: 2, md: 3 }, py: { xs: 3, md: 4 } }}>
      <Helmet>
        <title>{t("admin.pageTitle")}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Box sx={{ maxWidth: 1400, mx: "auto" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          gap={2}
          mb={3}
        >
          <Box>
            <Typography component="h1" variant="h4">
              {t("admin.title")}
            </Typography>
            <Typography color="text.secondary" mt={0.5}>
              {t("admin.subtitle")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            disabled={loading}
            sx={{ borderRadius: 999, px: 2.5 }}
          >
            {t("admin.createKey")}
          </Button>
        </Stack>

        {notice && (
          <Alert
            severity={notice.severity}
            onClose={() => setNotice(null)}
            sx={{ mb: 2 }}
          >
            {notice.text}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            border: "1px solid rgba(30, 41, 59, 1)",
            bgcolor: "#0f172a",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={tab}
            onChange={(_event, value: AdminTab) => setTab(value)}
            aria-label={t("admin.sections")}
            sx={{ px: 2, borderBottom: "1px solid rgba(30, 41, 59, 1)" }}
          >
            <Tab
              value="cities"
              label={t("admin.citiesTab", { count: organizations.length })}
              id="admin-tab-cities"
              aria-controls="admin-panel-cities"
            />
            <Tab
              value="accounts"
              label={t("admin.accountsTab", { count: members.length })}
              id="admin-tab-accounts"
              aria-controls="admin-panel-accounts"
            />
          </Tabs>
          <Box
            role="tabpanel"
            id={`admin-panel-${tab}`}
            aria-labelledby={`admin-tab-${tab}`}
            sx={{ p: { xs: 1.5, md: 2.5 } }}
          >
            {loading ? (
              <Box sx={{ py: 8, display: "grid", placeItems: "center" }}>
                <CircularProgress aria-label={t("admin.loading")} />
              </Box>
            ) : loadError ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => void load()}>
                    {t("common.retry")}
                  </Button>
                }
              >
                {loadError}
              </Alert>
            ) : tab === "cities" ? (
              <CitiesTable
                organizations={organizations}
                scanningId={scanningId}
                onRunScan={handleRunScan}
                onRevoke={(code, organization) =>
                  setPendingRevoke({ code, organization })
                }
              />
            ) : (
              <AccountsTable members={members} organizations={organizations} />
            )}
          </Box>
        </Paper>
      </Box>

      <CreateKeyDialog
        open={createOpen}
        organizations={organizations}
        onClose={() => setCreateOpen(false)}
        onCreated={() => void load()}
      />

      <Dialog
        open={Boolean(pendingRevoke)}
        onClose={revoking ? undefined : () => setPendingRevoke(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("admin.revokeTitle")}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            {t("admin.revokeBody", {
              name: pendingRevoke?.organization.name,
              label: pendingRevoke?.code.label || t("admin.untitledKey"),
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setPendingRevoke(null)} disabled={revoking}>
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmRevoke}
            disabled={revoking}
          >
            {revoking ? t("admin.revoking") : t("admin.revokeKey")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Admin;
